import { Injectable, SecurityContext } from '@angular/core';
import { Subject, firstValueFrom } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import { StoredContext } from '../../models/context';
import { formatDateAsYYYYMMDD } from '../../shared/helpers/date-helper';
import { CrmIntegrationModalService } from 'src/app/shared/services/crm-integration-modal.service';
import { ApiCrmService, CreateCrmTaskRequest, CrmIntegrationResponse } from 'src/app/shared/services/api/crm/api-crm.service';
import { UserSessionStore } from 'src/app/shared/services/user-session.store';

export type CalendarAction = 'google' | 'outlook' | 'hubspot' | 'salesforce';

@Injectable({
  providedIn: 'root',
})
export class TaskReminderService {
  private oauthWindowClosedSubject = new Subject<void>();
  oauthWindowClosed$ = this.oauthWindowClosedSubject.asObservable();

  constructor(
    private crmIntegrationModal: CrmIntegrationModalService,
    private apiCrmService: ApiCrmService,
    private sessionStore: UserSessionStore,
    private sanitizer: DomSanitizer
  ) {}

  async handleCalendarAction(action: CalendarAction, context: StoredContext): Promise<void> {
    switch (action) {
      case 'google':
        this.openGoogleCalendar(context);
        break;
      case 'outlook':
        this.openOutlookCalendar(context);
        break;
      case 'hubspot':
        await this.handleCrmAction('hubspot', context);
        break;
      case 'salesforce':
        await this.handleCrmAction('salesforce', context);
        break;
      default:
        console.warn(`Unknown calendar action: ${action}`);
    }
  }

  async getUserCrmIntegrations(): Promise<CrmIntegrationResponse[]> {
    const groupAccountId = this.sessionStore.userSession()?.groupAccountId;
    if (!groupAccountId) return [];
    return firstValueFrom(
      this.apiCrmService.getCrmData({
        fields: ['uid', 'type', 'scopes', 'instance_url', 'status'],
        filters: { group_account_id: groupAccountId },
        limit: 2,
        offset: 0,
      })
    );
  }

  private async handleCrmAction(crmType: 'hubspot' | 'salesforce', context: StoredContext): Promise<void> {
    const integrations = await this.getUserCrmIntegrations();
    const current = integrations[0];
    const isActiveForThisCrm = !!current && current.type === crmType && current.status === 'active';

    if (!isActiveForThisCrm) {
      const childWindow = await this.crmIntegrationModal.open(crmType);
      if (childWindow) {
        this.monitorChildWindowClose(childWindow);
      }
      return;
    }

    const { startDate } = this.calculateEventDates();
    const descriptionForCrm = this.buildEventDescription(context, crmType === 'salesforce' ? 'plain' : 'html');

    const payload: CreateCrmTaskRequest = {
      title: 'Rappel Explain',
      description: descriptionForCrm,
      due_date: startDate.toISOString(),
    };

    const result = await firstValueFrom(this.apiCrmService.createCrmTask(payload));
    if (result?.success && result.task_url) {
      window.open(result.task_url, '_blank');
    }
  }

  private monitorChildWindowClose(child: Window): void {
    const interval = setInterval(() => {
      try {
        if (child.closed) {
          clearInterval(interval);
          this.oauthWindowClosedSubject.next();
        }
      } catch (_) {
        if ((child as any).closed) {
          clearInterval(interval);
          this.oauthWindowClosedSubject.next();
        }
      }
    }, 500);
  }

  private openGoogleCalendar(context: StoredContext): void {
    const url = this.generateGoogleCalendarUrl(context);
    window.open(url, '_blank');
  }

  private openOutlookCalendar(context: StoredContext): void {
    const url = this.generateOutlookCalendarUrl(context);
    window.open(url, '_blank');
  }

  private generateGoogleCalendarUrl(context: StoredContext): string {
    const eventTitle = 'Rappel Explain';
    const eventDescription = this.buildEventDescription(context, 'html');
    const { startDate, endDate } = this.calculateEventDates();

    const baseUrl = 'https://calendar.google.com/calendar/render?action=TEMPLATE';
    const params = new URLSearchParams({
      text: eventTitle,
      dates: `${this.formatDateForGoogle(startDate)}/${this.formatDateForGoogle(endDate)}`,
      details: eventDescription,
    });

    return `${baseUrl}&${params.toString()}`;
  }

  private generateOutlookCalendarUrl(context: StoredContext): string {
    const eventTitle = 'Rappel Explain';
    const eventDescription = this.buildEventDescription(context, 'html');
    const { startDate, endDate } = this.calculateEventDates();

    const baseUrl = 'https://outlook.live.com/calendar/0/deeplink/compose';
    const params = new URLSearchParams({
      subject: eventTitle,
      body: eventDescription,
      startdt: this.formatDateForOutlook(startDate),
      enddt: this.formatDateForOutlook(endDate),
    });

    return `${baseUrl}?${params.toString()}`;
  }

  private calculateEventDates(): { startDate: Date; endDate: Date } {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() + 2);
    startDate.setHours(10, 30, 0, 0);

    const endDate = new Date(startDate);
    endDate.setMinutes(endDate.getMinutes() + 30);

    return { startDate, endDate };
  }

  private buildEventDescription(context: StoredContext, format: 'html' | 'plain' = 'html'): string {
    const joiner = format === 'html' ? '<br><br>' : '\n\n';
    const parts: string[] = [];

    if (context.title) {
      parts.push(`Resumé: ${context.title.charAt(0).toUpperCase() + context.title.slice(1)}`);
    }

    if (context.snippet) {
      const snippet = format === 'html' ? context.snippet : this.sanitizeToPlainText(context.snippet);
      parts.push(`Extrait: ${snippet}`);
    }

    if (context.url) {
      const urlPart =
        format === 'html' ? `Lien Explain: <a href="${context.url}" target="_blank">${context.url}</a>` : `Lien Explain: ${context.url}`;
      parts.push(urlPart);
    }

    const composed = parts.join(joiner);

    if (format === 'html') {
      const sanitized = this.sanitizer.sanitize(SecurityContext.HTML, composed) || '';
      return sanitized;
    }

    return composed;
  }

  private sanitizeToPlainText(input: string): string {
    if (!input) return '';
    const withNewlines = input.replace(/<br\s*\/?\s*>/gi, '\n');
    const sanitized = this.sanitizer.sanitize(SecurityContext.HTML, withNewlines) || '';
    const textarea = document.createElement('textarea');
    textarea.innerHTML = sanitized;
    const decoded = textarea.value;
    return decoded.replace(/[ \t]*\n[ \t]*/g, '\n').trim();
  }

  private formatDateForGoogle(date: Date): string {
    const baseFormat = formatDateAsYYYYMMDD(date);
    const time = date.toISOString().split('T')[1].replace(/[-:]/g, '').split('.')[0];
    return baseFormat.replace(/-/g, '') + 'T' + time + 'Z';
  }

  private formatDateForOutlook(date: Date): string {
    const baseFormat = formatDateAsYYYYMMDD(date);
    const time = date.toTimeString().split(' ')[0];
    return baseFormat + 'T' + time;
  }
}
